import { useState, useEffect, useRef } from "react";
import { getIn, useFormikContext } from "formik";
import { Box, Flex, Text, Button, TextField, Select } from "@radix-ui/themes";
import SearchableSelect from "@/components/ui/SearchableSelect";
import {
  buildCron,
  DEFAULT_SCHEDULE,
  HOUR_INTERVALS,
  MAX_MONTH_DAY,
  MINUTE_INTERVALS,
  MINUTE_OPTIONS,
  parseCron,
  ScheduleType,
} from "./scheduleCron";

const DAYS_OF_WEEK = [
  { label: "Mon", value: "1" },
  { label: "Tue", value: "2" },
  { label: "Wed", value: "3" },
  { label: "Thu", value: "4" },
  { label: "Fri", value: "5" },
  { label: "Sat", value: "6" },
  { label: "Sun", value: "0" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString(),
  label: `${i.toString().padStart(2, "0")}:00`,
}));

function cronToDescription(cron: string): string {
  if (!cron || cron === "* * * * *") return "Every minute";
  const parts = cron.split(" ");
  if (parts.length !== 5) return cron;
  const [min, hour, day, , weekday] = parts;

  if (min.startsWith("*/")) return `Every ${min.slice(2)} minutes`;
  if (hour.startsWith("*/")) return `Every ${hour.slice(2)} hours`;
  if (day !== "*" && weekday === "*")
    return `${day}${day === "1" ? "st" : day === "2" ? "nd" : day === "3" ? "rd" : "th"} of each month at ${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
  if (weekday !== "*") {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const days = weekday
      .split(",")
      .map((d) => {
        if (d.includes("-")) {
          const [start, end] = d.split("-").map(Number);
          return `${dayNames[start]}-${dayNames[end]}`;
        }
        return dayNames[Number(d)] || d;
      })
      .join(", ");
    return `${days} at ${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
  }
  if (hour !== "*" && min !== "*")
    return `Daily at ${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
  return cron;
}

export default function ScheduleBuilder({ name }: { name: string }) {
  const { values, setFieldValue } = useFormikContext<any>();
  const currentCron: string = getIn(values, name) || "";

  const [useCron, setUseCron] = useState(
    () => !!currentCron && !parseCron(currentCron),
  );
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    DEFAULT_SCHEDULE.type,
  );
  const [intervalUnit, setIntervalUnit] = useState<"minutes" | "hours">(
    DEFAULT_SCHEDULE.intervalUnit,
  );
  const [intervalValue, setIntervalValue] = useState(
    DEFAULT_SCHEDULE.intervalValue,
  );
  const [hour, setHour] = useState(DEFAULT_SCHEDULE.hour);
  const [minute, setMinute] = useState(DEFAULT_SCHEDULE.minute);
  const [selectedDays, setSelectedDays] = useState<string[]>(
    DEFAULT_SCHEDULE.days,
  );
  const [monthDay, setMonthDay] = useState(DEFAULT_SCHEDULE.monthDay);

  // Seeds a brand-new trigger so save doesn't 400; opening must never replace an existing schedule.
  const hasSeededDefault = useRef(!!currentCron);
  useEffect(() => {
    if (currentCron || hasSeededDefault.current) return;
    hasSeededDefault.current = true;
    setFieldValue(name, buildCron(DEFAULT_SCHEDULE));
  }, [currentCron, name, setFieldValue]);

  useEffect(() => {
    const parsed = parseCron(currentCron);
    if (!parsed) return;
    setScheduleType(parsed.type);
    setIntervalUnit(parsed.intervalUnit);
    setIntervalValue(parsed.intervalValue);
    setHour(parsed.hour);
    setMinute(parsed.minute);
    setSelectedDays(parsed.days);
    setMonthDay(parsed.monthDay);
  }, [currentCron]);

  const handleChange = (
    updates: Partial<{
      type: ScheduleType;
      intervalUnit: "minutes" | "hours";
      intervalValue: number;
      hour: string;
      minute: string;
      days: string[];
      monthDay: string;
    }>,
  ) => {
    const t = updates.type ?? scheduleType;
    const iu = updates.intervalUnit ?? intervalUnit;
    const iv = updates.intervalValue ?? intervalValue;
    const h = updates.hour ?? hour;
    const m = updates.minute ?? minute;
    const d = updates.days ?? selectedDays;
    const md = updates.monthDay ?? monthDay;

    if (updates.type !== undefined) setScheduleType(t);
    if (updates.intervalUnit !== undefined) setIntervalUnit(iu);
    if (updates.intervalValue !== undefined) setIntervalValue(iv);
    if (updates.hour !== undefined) setHour(h);
    if (updates.minute !== undefined) setMinute(m);
    if (updates.days !== undefined) setSelectedDays(d);
    if (updates.monthDay !== undefined) setMonthDay(md);

    setFieldValue(
      name,
      buildCron({
        type: t,
        intervalUnit: iu,
        intervalValue: iv,
        hour: h,
        minute: m,
        days: d,
        monthDay: md,
      }),
    );
  };

  return (
    <Box>
      <Flex justify="between" align="center" mb="2">
        <Text size="2" weight="medium">
          Schedule
        </Text>
        <Button
          variant="ghost"
          size="1"
          onClick={() => setUseCron(!useCron)}
          style={{ fontSize: "11px", padding: "2px 6px" }}
          type="button"
        >
          {useCron ? "Use scheduler" : "Use cron expression"}
        </Button>
      </Flex>

      {useCron ? (
        <Box>
          <TextField.Root
            value={currentCron}
            onChange={(e) => setFieldValue(name, e.target.value)}
            placeholder="* * * * *"
          />
          <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
            Format: minute hour day month weekday
          </Text>
        </Box>
      ) : (
        <Flex direction="column" gap="3">
          <Flex gap="2">
            {(["interval", "daily", "weekly", "monthly"] as ScheduleType[]).map(
              (type) => (
                <Button
                  key={type}
                  size="1"
                  variant={scheduleType === type ? "solid" : "soft"}
                  onClick={() => handleChange({ type })}
                  style={{ textTransform: "capitalize" }}
                  type="button"
                >
                  {type}
                </Button>
              ),
            )}
          </Flex>

          {scheduleType === "interval" && (
            <Flex gap="2" align="end">
              <Box style={{ flex: 1 }}>
                <Text
                  size="1"
                  weight="medium"
                  color="gray"
                  mb="1"
                  style={{ display: "block" }}
                >
                  Every
                </Text>
                <Select.Root
                  value={intervalValue.toString()}
                  onValueChange={(v) =>
                    handleChange({ intervalValue: Number(v) })
                  }
                >
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content
                    container={
                      typeof document !== "undefined"
                        ? document.body
                        : undefined
                    }
                    style={{ zIndex: 12000 }}
                  >
                    {(intervalUnit === "minutes"
                      ? MINUTE_INTERVALS
                      : HOUR_INTERVALS
                    ).map((v) => (
                      <Select.Item key={v} value={v.toString()}>
                        {v}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Box>
              <Box style={{ flex: 1 }}>
                <Select.Root
                  value={intervalUnit}
                  onValueChange={(v) =>
                    handleChange({ intervalUnit: v as "minutes" | "hours" })
                  }
                >
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content
                    container={
                      typeof document !== "undefined"
                        ? document.body
                        : undefined
                    }
                    style={{ zIndex: 12000 }}
                  >
                    <Select.Item value="minutes">Minutes</Select.Item>
                    <Select.Item value="hours">Hours</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Box>
            </Flex>
          )}

          {["daily", "weekly", "monthly"].includes(scheduleType) && (
            <Flex gap="2" align="end">
              <Box style={{ flex: 1 }}>
                <Text
                  size="1"
                  weight="medium"
                  color="gray"
                  mb="1"
                  style={{ display: "block" }}
                >
                  At
                </Text>
                <SearchableSelect
                  value={hour}
                  onChange={(v) => handleChange({ hour: v })}
                  options={HOURS}
                  searchPlaceholder="Search time..."
                  width={200}
                />
              </Box>
              <Box style={{ width: 100 }}>
                <Text
                  size="1"
                  weight="medium"
                  color="gray"
                  mb="1"
                  style={{ display: "block" }}
                >
                  Min
                </Text>
                <Select.Root
                  value={minute}
                  onValueChange={(v) => handleChange({ minute: v })}
                >
                  <Select.Trigger style={{ width: "100%" }} />
                  <Select.Content
                    container={
                      typeof document !== "undefined"
                        ? document.body
                        : undefined
                    }
                    style={{ zIndex: 12000 }}
                  >
                    {MINUTE_OPTIONS.map((m) => (
                      <Select.Item key={m} value={m.toString()}>
                        {m.toString().padStart(2, "0")}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Box>
            </Flex>
          )}

          {scheduleType === "weekly" && (
            <Box>
              <Text
                size="1"
                weight="medium"
                color="gray"
                mb="1"
                style={{ display: "block" }}
              >
                On
              </Text>
              <Flex gap="1">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day.value}
                    size="1"
                    variant={
                      selectedDays.includes(day.value) ? "solid" : "outline"
                    }
                    onClick={() => {
                      const next = selectedDays.includes(day.value)
                        ? selectedDays.filter((d) => d !== day.value)
                        : [...selectedDays, day.value];
                      if (next.length > 0) handleChange({ days: next });
                    }}
                    style={{ minWidth: 40 }}
                    type="button"
                  >
                    {day.label}
                  </Button>
                ))}
              </Flex>
            </Box>
          )}

          {scheduleType === "monthly" && (
            <Box>
              <Text
                size="1"
                weight="medium"
                color="gray"
                mb="1"
                style={{ display: "block" }}
              >
                Day of month
              </Text>
              <Select.Root
                value={monthDay}
                onValueChange={(v) => handleChange({ monthDay: v })}
              >
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content
                  container={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  style={{ zIndex: 12000 }}
                >
                  {Array.from({ length: MAX_MONTH_DAY }, (_, i) => i + 1).map((d) => (
                    <Select.Item key={d} value={d.toString()}>
                      {d}
                      {d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : "th"}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
          )}

          {currentCron && (
            <Box
              p="2"
              style={{ backgroundColor: "var(--gray-3)", borderRadius: 4 }}
            >
              <Text size="1" color="gray">
                {cronToDescription(currentCron)}{" "}
              </Text>
              <Text
                size="1"
                style={{ fontFamily: "monospace", color: "var(--gray-9)" }}
              >
                ({currentCron})
              </Text>
            </Box>
          )}
        </Flex>
      )}
    </Box>
  );
}
